var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { OnInit, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { AuthenticationService } from '../../../core/services/authentication.service';
import { ActivatedRoute, Router } from '@angular/router';
import { TeamService } from '../../../core/services/team.service';
import { TeamTemplateComponent } from '../../../core/generic/team-template.component';
import { ToastMsg } from 'src/app/modules/core/models/ngprime/toastMsg';
import { ToastService } from 'src/app/modules/core/services/toast.service';
import { PortalBootstrapService } from '../../../core/services/portal-bootstrap.service';
import { Auth0ClientService } from '../../../core/services/auth0-client.service';
import { TranslateService } from '@ngx-translate/core';
export class AuthPageComponent extends TeamTemplateComponent {
    constructor(fb, templateService, authenticationService, router, toastService, activatedRoute, portalBootstrap, auth0Client, translate, changeDetectorRef) {
        super(templateService);
        this.authenticationService = authenticationService;
        this.router = router;
        this.toastService = toastService;
        this.activatedRoute = activatedRoute;
        this.portalBootstrap = portalBootstrap;
        this.auth0Client = auth0Client;
        this.translate = translate;
        this.changeDetectorRef = changeDetectorRef;
        this.isLoading = false;
        this.loadingText = 'Loading';
        this.auth0Enabled = false;
        this.form = fb.group({
            email: ['', Validators.required],
            password: ['', Validators.required]
        });
        this.template = templateService.teamTemplate;
    }
    ngOnInit() {
        // Get returnUrl from route parameters or default to main page
        this.returnUrl = this.activatedRoute.snapshot.queryParams['returnUrl'] || `${this.template.teamId}/main`;
        this.auth0Enabled = this.portalBootstrap.auth0Enabled;
        if (this.auth0Enabled) {
            // Show the landing page (instructions + Sign In / Sign Up) instead of
            // auto-redirecting to Auth0, so users know they must sign up before signing in.
            this.instructionSteps$ = this.translate.stream('pages.auth0Landing.steps');
            return;
        }
        this.authenticationService.isLoggedIn().subscribe(isLoggedIn => {
            if (isLoggedIn) {
                this.router.navigate([this.returnUrl]);
            }
        });
    }
    // After Sign In / Sign Up we set isLoading=true and do a full-page redirect to Auth0. Pressing
    // the browser Back button restores this page from the back-forward cache (bfcache) with the
    // frozen spinner still showing — ngOnInit does not re-run on restore. Clearing isLoading alone
    // is NOT enough: change detection does not reliably run on a bfcache restore, so the overlay's
    // [hidden]="!isLoading" binding is never re-evaluated. We therefore clear the flag AND force a
    // change-detection pass. Keyed on isLoading being stuck (rather than event.persisted) so it
    // works regardless of how the browser reports the restore.
    onPageShow() {
        if (this.isLoading) {
            this.isLoading = false;
            this.changeDetectorRef.detectChanges();
        }
    }
    submit() {
        if (!this.form.valid) {
            this.form.markAllAsTouched();
            return;
        }
        this.isLoading = true;
        const request = this.form.getRawValue();
        this.authenticationService.login(request).subscribe((loginResult) => __awaiter(this, void 0, void 0, function* () {
            if (!loginResult.isSuccess) {
                this.isLoading = false;
                return;
            }
            if (!this.authenticationService.isValidatedAccount(this.template.teamId)) {
                // Preserve returnUrl when redirecting to change password
                yield this.router.navigate([`${this.template.teamId}/main/change-password`], { queryParams: { returnUrl: this.returnUrl } });
                return;
            }
            this.authenticationService.checkCustomersDataValidation()
                .subscribe(((customerDataValidationResult) => __awaiter(this, void 0, void 0, function* () {
                if (!customerDataValidationResult.isSuccess) {
                    this.toastService.showError(new ToastMsg({ detail: customerDataValidationResult.errorMessage }));
                }
                if (customerDataValidationResult.data && customerDataValidationResult.data.length > 0) {
                    const message = customerDataValidationResult.data.map(x => x.fieldName).join(', ');
                    this.toastService.showWarning(new ToastMsg({ detail: message }));
                    this.toastService.showWarning(new ToastMsg({ detail: 'There are fields that are not set in your profile, please consider going to profile and edit them' }));
                    // Preserve returnUrl when redirecting to profile
                    yield this.router.navigate([`${this.template.teamId}/main/profile`], { queryParams: { returnUrl: this.returnUrl } });
                }
                else {
                    yield this.router.navigate([this.returnUrl]);
                }
                this.isLoading = false;
            })));
        }));
    }
    // Auth0 landing-page action: start the Auth0 login (sign-in) experience.
    signIn() {
        this.isLoading = true;
        this.auth0Client.loginWithRedirect(this.returnUrl).catch(() => {
            this.isLoading = false;
            this.toastService.showError(new ToastMsg({ detail: 'Unable to start sign-in. Please try again.' }));
        });
    }
    // Auth0 landing-page action: start the Auth0 signup experience (screen_hint=signup).
    signUp() {
        this.isLoading = true;
        this.auth0Client.loginWithRedirect(this.returnUrl, true).catch(() => {
            this.isLoading = false;
            this.toastService.showError(new ToastMsg({ detail: 'Unable to start sign-up. Please try again.' }));
        });
    }
    register() {
        this.router.navigate(['steps'], { relativeTo: this.activatedRoute.parent });
    }
    forgotPassword() {
        this.router.navigate(['forgot-password'], { relativeTo: this.activatedRoute.parent });
    }
}
